import { ToolOption } from 'src/app/components/ai/chat/interfaces/chat-component.interfaces';
import { AgentCatalogEntry, getAgentEntry } from './agent-catalog';

export type ToolSelectionMode = 'all' | 'defaults' | 'custom';

const CATEGORY_DEFAULT_TOOL_GROUPS: Record<AgentCatalogEntry['category'], string[]> = {
  market: ['businesses', 'notes'],
  sell: ['businesses', 'proposal'],
  plan: ['plan', 'businesses'],
  build: ['tasks', 'files'],
  support: ['tickets', 'notes']
};

const AGENT_DEFAULT_TOOL_GROUPS: Record<string, string[]> = {
  marketing: ['businesses', 'notes'],
  sdr: ['businesses', 'notes'],
  'account-executive': ['businesses', 'proposal'],
  'solutions-architect': ['plan', 'businesses'],
  'product-manager': ['plan', 'notes'],
  'product-designer': ['plan', 'files'],
  'project-manager': ['tasks', 'plan'],
  'full-stack-engineering': ['tasks', 'files', 'app'],
  'frontend-developer': ['tasks', 'files'],
  'backend-developer': ['tasks', 'app'],
  'qa-tester': ['tasks', 'files'],
  'devops-sre': ['tasks', 'files'],
  'customer-success-manager': ['tickets', 'notes'],
  'legal-compliance': ['proposal', 'notes'],
  'finance-billing': ['purchases', 'notes']
};

export function normalizeToolNameList(toolNames: string[]): string[] {
  const seen = new Set<string>();

  return toolNames
    .map(name => typeof name === 'string' ? name.trim() : '')
    .filter(name => {
      if (!name || seen.has(name)) {
        return false;
      }

      seen.add(name);
      return true;
    })
    .sort((left, right) => left.localeCompare(right));
}

export function areToolSelectionsEqual(left: string[], right: string[]): boolean {
  const normalizedLeft = normalizeToolNameList(left);
  const normalizedRight = normalizeToolNameList(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((toolName, index) => toolName === normalizedRight[index]);
}

export function inferToolSelectionMode(selectedToolNames: string[], defaultToolNames: string[]): ToolSelectionMode {
  if (selectedToolNames.length === 0) {
    return 'all';
  }

  return areToolSelectionsEqual(selectedToolNames, defaultToolNames) ? 'defaults' : 'custom';
}

export function getToolNamesForGroups(groupKeys: string[], tools: ToolOption[]): string[] {
  if (groupKeys.length === 0 || tools.length === 0) {
    return [];
  }

  const groupKeySet = new Set(
    groupKeys
      .map(groupKey => typeof groupKey === 'string' ? groupKey.trim() : '')
      .filter(Boolean)
  );

  if (groupKeySet.size === 0) {
    return [];
  }

  return normalizeToolNameList(
    tools
      .filter(tool => groupKeySet.has((tool.groupKey || tool.category || '').trim()))
      .map(tool => tool.name)
  );
}

export function getDefaultToolNamesForAgent(agentKey: string | null | undefined, tools: ToolOption[]): string[] {
  const agent = getAgentEntry(agentKey);
  const groupKeys = agent
    ? AGENT_DEFAULT_TOOL_GROUPS[agent.slug] || CATEGORY_DEFAULT_TOOL_GROUPS[agent.category] || []
    : [];

  return getToolNamesForGroups(groupKeys, tools);
}